import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useState, lazy, Suspense } from "react";

import "@/App.css";
import SmoothScroll from "@/components/site/SmoothScroll";
import NoiseOverlay from "@/components/site/NoiseOverlay";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { fetchSiteSettings, trackPageview } from "@/lib/api";
import { captureUtm } from "@/lib/utm";
import { useQuery } from "@tanstack/react-query";

import CookieConsent from "@/components/site/CookieConsent";
import { lazyPreloadable } from "@/lib/lazyPreload";

const Home = lazyPreloadable(() => import("@/pages/Home"));
const Diensten = lazyPreloadable(() => import("@/pages/Diensten"));
const OverMij = lazyPreloadable(() => import("@/pages/OverMij"));
const NotFound = lazyPreloadable(() => import("@/pages/NotFound"));
const Cases = lazyPreloadable(() => import("@/pages/Cases"));
const CaseDetail = lazyPreloadable(() => import("@/pages/CaseDetail"));
const Blog = lazyPreloadable(() => import("@/pages/Blog"));
const BlogPost = lazyPreloadable(() => import("@/pages/BlogPost"));
const Contact = lazyPreloadable(() => import("@/pages/Contact"));
const Page = lazyPreloadable(() => import("@/pages/Page"));
const ServicePage = lazyPreloadable(() => import("@/pages/ServicePage"));
const DigitaliseringsScan = lazyPreloadable(() => import("@/pages/DigitaliseringsScan"));
const AdminLogin = lazyPreloadable(() => import("@/pages/AdminLogin"));
const AuthCallback = lazyPreloadable(() => import("@/pages/AuthCallback"));
const Admin = lazyPreloadable(() => import("@/pages/Admin"));

const AiAssistant = lazy(() => import("@/components/site/AiAssistant"));
const Toaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

// Route-chunk van het huidige pad vooraf laden zodat de eerste render niet suspendt (geen lege fallback-flits).
const ROUTE_CHUNKS = [
  [/^\/$/, Home],
  [/^\/diensten$/, Diensten],
  [/^\/diensten\//, ServicePage],
  [/^\/over-mij$/, OverMij],
  [/^\/cases$/, Cases],
  [/^\/cases\//, CaseDetail],
  [/^\/blog$/, Blog],
  [/^\/blog\//, BlogPost],
  [/^\/contact$/, Contact],
  [/^\/digitaliseringsscan$/, DigitaliseringsScan],
  [/^\/admin\/login/, AdminLogin],
  [/^\/auth\/callback/, AuthCallback],
  [/^\/admin/, Admin],
  [/./, Page],
];
export function preloadRoute(pathname) {
  const p = pathname.replace(/\/+$/, "") || "/";
  const hit = ROUTE_CHUNKS.find(([re]) => re.test(p));
  return hit ? hit[1].preload().catch(() => null) : Promise.resolve();
}

// Mount niet-kritieke UI (chatknop, toasts) pas bij de eerste interactie of na 5 s
function Deferred({ children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    const evs = ["pointerdown", "keydown", "touchstart", "wheel", "scroll"];
    evs.forEach((ev) => window.addEventListener(ev, go, { passive: true, once: true }));
    const id = setTimeout(go, 5000);
    return () => {
      evs.forEach((ev) => window.removeEventListener(ev, go));
      clearTimeout(id);
    };
  }, []);
  return ready ? <Suspense fallback={null}>{children}</Suspense> : null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    let sid = sessionStorage.getItem("ecell_sid");
    if (!sid) {
      sid = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
      sessionStorage.setItem("ecell_sid", sid);
    }
    const utm = captureUtm();
    trackPageview({
      path: pathname,
      referrer: document.referrer || "",
      session_id: sid,
      device: window.innerWidth < 768 ? "mobile" : "desktop",
      ...utm,
    }).catch(() => {});
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", { page_path: pathname, page_location: window.location.href });
    }
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink text-bone-muted">
        Laden…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin)
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink text-bone-muted p-8">
        Geen toegang tot admin.
      </div>
    );
  return children;
}

function SiteShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const { data: settings } = useQuery({
    queryKey: ["site-settings"],
    queryFn: fetchSiteSettings,
  });

  return (
    <>
      <NoiseOverlay />
      <PageTracker />
      {!isAdmin && <Navbar />}
      <main className="relative z-10">
        <Suspense fallback={<div className="min-h-screen bg-ink" />}>
        <Routes>
          <Route path="/" element={<Home settings={settings} />} />
          <Route path="/diensten" element={<Diensten />} />
          <Route path="/diensten/:slug" element={<ServicePage />} />
          <Route path="/over-mij" element={<OverMij settings={settings} />} />
          <Route path="/cases" element={<Cases />} />
          <Route path="/cases/:slug" element={<CaseDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact settings={settings} />} />
          <Route path="/digitaliseringsscan" element={<DigitaliseringsScan />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route path="/:slug" element={<Page />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      {!isAdmin && <Footer settings={settings} />}
      {!isAdmin && <CookieConsent gaId={settings?.ga4_measurement_id} />}
      {!isAdmin && (
        <Deferred>
          <AiAssistant />
        </Deferred>
      )}
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: session_id arrives in URL fragment after Google OAuth — handle FIRST.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={null}>
        <AuthCallback />
      </Suspense>
    );
  }
  return <SiteShell />;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll>
            <div className="bg-ink text-bone min-h-screen font-sans">
              <ScrollToTop />
              <AppRouter />
              <Deferred>
                <Toaster
                  theme="light"
                  position="bottom-right"
                  toastOptions={{
                    style: {
                      background: "#FFFFFF",
                      border: "1px solid rgba(19,41,61,0.12)",
                      color: "#13293D",
                    },
                  }}
                />
              </Deferred>
            </div>
          </SmoothScroll>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
